import { z } from 'zod'
import { changedOnSchema } from './change-made.input.js'
import { siteMapStatusSchema } from '../enums/site-map-status.enum.js'
import { siteBoundarySchema } from '../dto/geo.dto.js'
import {
  BOUNDARY_PROBLEM_MESSAGES,
  findBoundaryProblem,
} from '../validators/polygon.validator.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/*
 * There is deliberately no `acreage` field here.
 *
 * A site's acreage is computed from its boundary, so accepting one would be
 * accepting a second answer to a question that already has one — and the two
 * would disagree the first time somebody redrew an outline without retyping a
 * number. Draw the boundary; the acreage follows.
 */

/**
 * A boundary on the way in, checked for structural soundness as well as shape.
 *
 * Nullable because clearing an outline is a legitimate edit: a site can be
 * re-surveyed, or traced wrong and started over.
 */
const boundaryInputSchema = siteBoundarySchema
  .superRefine((boundary, context) => {
    const problem = findBoundaryProblem(boundary.vertices)
    if (problem === null) return
    context.addIssue({
      code: 'custom',
      message: BOUNDARY_PROBLEM_MESSAGES[problem],
      path: ['vertices'],
    })
  })
  .nullable()

export const createSiteInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Site name is required').max(200),

    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),

    mapStatus: siteMapStatusSchema.optional(),
    boundary: boundaryInputSchema.optional(),
    notes: optionalText(5000),
  })
  .strict()

export type CreateSiteInput = z.infer<typeof createSiteInputSchema>

export const updateSiteInputSchema = createSiteInputSchema
  .partial()
  .extend({
    /**
     * The answer to "Was it wrong, or did it change?" for a redrawn
     * boundary, asked once the site has been visited: true for "Was drawn
     * wrong". Left out when nothing asked it.
     */
    isCorrection: z.boolean().optional(),
    /** When it changed, for a change answered "Changed". */
    changedOn: changedOnSchema,
  })
  .strict()

export type UpdateSiteInput = z.infer<typeof updateSiteInputSchema>
